import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [kiosk, setKiosk] = useState(null);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    const savedKiosk = localStorage.getItem('cartKiosk');
    
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
    
    if (savedKiosk) {
      try {
        setKiosk(JSON.parse(savedKiosk));
      } catch (e) {
        console.error('Error loading kiosk:', e);
      }
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    if (kiosk) {
      localStorage.setItem('cartKiosk', JSON.stringify(kiosk));
    } else {
      localStorage.removeItem('cartKiosk');
    }
  }, [cart, kiosk]);

  const addItem = (product, quantity = 1, notes = '') => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.product_id === product.id);
      
      if (existingItem) {
        return prevCart.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity, notes }
            : item
        );
      }
      
      return [
        ...prevCart,
        {
          product_id: product.id,
          product_name: product.name,
          product_price: product.price,
          quantity,
          subtotal: product.price * quantity,
          notes,
        },
      ];
    });
  };

  const removeItem = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.product_id === productId
          ? {
              ...item,
              quantity,
              subtotal: item.product_price * quantity,
            }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setKiosk(null);
  };

  const setCartKiosk = (newKiosk) => {
    if (kiosk && kiosk.id !== newKiosk.id) {
      // Different kiosk, clear cart
      setCart([]);
    }
    setKiosk(newKiosk);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const getItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const value = {
    cart,
    kiosk,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setCartKiosk,
    getTotal,
    getItemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
